import { Box, Stack, Grid } from "@mantine/core"
import { useForm } from "@mantine/form"

import { RenderFormSection } from "~/pages/Admin/components/RenderFormSection"
import { TypesFormActionsSection } from "~/pages/Admin/components/TypesFormActionsSection"
import {
  FormConfig,
  FormElement,
  isFormGroupConfig,
} from "~/pages/Admin/components/form.types"

export type TypesFormFooterVariant = "default" | "pinned"

export type TypesFormProps<T> = {
  elements: FormElement[]
  submitLabel: string
  formConfig: FormConfig<T>
  mutate: (values: T) => void
  closeDrawer?: () => void
  cancelLabel?: string
  children?: React.ReactNode
  readonly?: boolean
  /**
   * `pinned` matches SchemaForm drawer footer: top border, Cancel/Save,
   * optional Delete, Save never disabled (field errors on submit).
   * Default keeps legacy EqualWidthGrid + disabled-until-valid Save.
   */
  footerVariant?: TypesFormFooterVariant
  deleteLabel?: string
  handleDelete?: () => void
  isLoading?: boolean
  /** When false, hides Cancel in the pinned footer (e.g. edit mode). Default true. */
  showCancel?: boolean
}

export const TypesForm = <T extends Record<string, unknown>>(
  props: TypesFormProps<T>
) => {
  const footerVariant = props.footerVariant ?? "default"
  const isPinnedFooter = footerVariant === "pinned"

  const form = useForm({
    ...props.formConfig,
    validateInputOnChange: true,
  })

  const handleSubmit = () => {
    props.mutate(form.values)
    form.reset()
    if (props.closeDrawer) {
      props.closeDrawer()
    }
  }

  const isElementHidden = (element: FormElement): boolean => {
    if ("hidden" in element && element.hidden === true) {
      return true
    }

    if (isFormGroupConfig(element)) {
      if (element.isHidden !== undefined) {
        return element.isHidden(form.values)
      }
    }

    return false
  }

  const formFields = (
    <>
      <Grid px="var(--tfg-layout-padding-x)">
        {props.elements.map(
          (element, index) =>
            !isElementHidden(element) && (
              <RenderFormSection
                key={`${element.type}-${index}`}
                element={element}
                form={form}
                index={index}
              />
            )
        )}
      </Grid>
      {props.children && (
        <Box px="var(--tfg-layout-padding-x)">{props.children}</Box>
      )}
    </>
  )

  return (
    <form
      onSubmit={form.onSubmit(() => handleSubmit())}
      style={
        isPinnedFooter
          ? {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }
          : undefined
      }
    >
      {isPinnedFooter ? (
        <Stack gap="sm" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {formFields}
        </Stack>
      ) : (
        <Stack h="100%" gap="sm">
          {formFields}
        </Stack>
      )}
      <TypesFormActionsSection
        form={form}
        closeDrawer={props.closeDrawer}
        cancelLabel={props.cancelLabel}
        submitLabel={props.submitLabel}
        readonly={props.readonly}
        footerVariant={footerVariant}
        deleteLabel={props.deleteLabel}
        handleDelete={props.handleDelete}
        isLoading={props.isLoading}
        showCancel={props.showCancel}
      />
    </form>
  )
}
